import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
  Button,
  Slider,
} from '@mui/material';
import { Refresh, Stop } from '@mui/icons-material';
import api from '../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DecisionLogEntry {
  ts: string;
  grade: string;
  cloudTrend: string;
  cloudSignal: string;
  domSignal: string;
  recommendedAction: string;
  lastClose: number;
  livePrice?: number;
  chainAllowed: boolean;
  blockedAt: string | null;
  chainReason: string;
  action: string;
  openLots: number;
  clipId?: string;
}

interface RunnerStatus {
  isRunning: boolean;
  isStopped: boolean;
  accountId: string;
  contractId: string;
  maxLots: number;
  openLots: number;
  clips: Array<{ clipId: string; entryPrice: number; lots: number; entryTime: number }>;
  lastSignalFetch: string | null;
  cachedSignal: {
    grade: string;
    cloudTrend: string;
    cloudSignal: string;
    domSignal: string;
    recommendedAction: string;
    lastClose: number;
    signalAge: string;
    error: string | null;
  } | null;
  persistedWindow: {
    winningDays150: number;
    tradedDays: number;
    largestWinningDay: number;
    totalWindowProfit: number;
    balance: number;
    netProfitSinceLastPayout: number;
  };
  decisionLog: DecisionLogEntry[];
}

// ── Colour helpers ────────────────────────────────────────────────────────────

const ACTION_COLORS: Record<string, string> = {
  CLIP_ADDED:     '#4caf50',
  TP_TAKEN:       '#66bb6a',
  WAIT:           '#90caf9',
  REFUSED:        '#ef9a9a',
  CLOUD_BREAK:    '#ff7043',
  POSITION_STOP:  '#f44336',
  SOVEREIGN_HALT: '#b71c1c',
  SIGNAL_ERROR:   '#ff8f00',
  EXIT_ALL:       '#e65100',
};

const CLOUD_COLORS: Record<string, string> = {
  bull_cloud:  '#4caf50',
  bear_cloud:  '#ef5350',
  flat:        '#90a4ae',
  'N/A':       '#546e7a',
};

const GRADE_COLORS: Record<string, string> = {
  A_A:     '#4caf50',
  B_A:     '#8bc34a',
  A_B:     '#cddc39',
  C_C:     '#ffa726',
  C_F:     '#ef5350',
  WAIT:    '#90caf9',
  NO_TRADE:'#78909c',
  ERROR:   '#ff8f00',
  'N/A':   '#546e7a',
};

function colorChip(label: string, colorMap: Record<string, string>, fallback = '#607d8b') {
  const color = colorMap[label] ?? fallback;
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: color + '22',
        color,
        border: `1px solid ${color}55`,
        fontSize: '0.68rem',
        height: 20,
        fontWeight: 600,
      }}
    />
  );
}

function formatTime(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return iso; }
}

// ── Status bar + controls ─────────────────────────────────────────────────────

interface RunnerControlsProps {
  status: RunnerStatus;
  accountId?: number;
  onRefresh: () => void;
}

function RunnerStatusBar({ status, accountId, onRefresh }: RunnerControlsProps) {
  const sig = status.cachedSignal;
  const isLive = status.isRunning && !status.isStopped;
  const [stopping, setStopping]         = useState(false);
  const [localMaxLots, setLocalMaxLots] = useState<number>(status.maxLots ?? 7);
  const [savingLots, setSavingLots]     = useState(false);
  const [lotMsg, setLotMsg]             = useState<string | null>(null);
  const debounceRef                     = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync slider if server-reported maxLots changes (e.g. after restart)
  useEffect(() => {
    if (status.maxLots != null) setLocalMaxLots(status.maxLots);
  }, [status.maxLots]);

  const handleStop = async () => {
    if (!accountId) return;
    setStopping(true);
    try {
      await api.post(`/topstep/profile-c/runners/${accountId}/stop`);
      onRefresh();
    } catch { /* error visible via next poll */ } finally {
      setStopping(false);
    }
  };

  const handleLotChange = (_: Event, value: number | number[]) => {
    const n = value as number;
    setLocalMaxLots(n);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!accountId) return;
      setSavingLots(true);
      setLotMsg(null);
      try {
        await api.post(`/topstep/profile-c/runners/${accountId}/max-lots`, { maxLots: n });
        setLotMsg(`Set to ${n}`);
        setTimeout(() => setLotMsg(null), 2000);
      } catch { setLotMsg('error'); } finally { setSavingLots(false); }
    }, 400);
  };

  return (
    <Box sx={{ mb: 2 }}>
      {/* Row 1: status chips + signal */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
        <Chip
          label={isLive ? 'LIVE' : 'STOPPED'}
          size="small"
          sx={{
            bgcolor: isLive ? '#4caf5022' : '#ef535022',
            color:   isLive ? '#4caf50'   : '#ef5350',
            border: `1px solid ${isLive ? '#4caf5055' : '#ef535055'}`,
            fontWeight: 700,
          }}
        />
        <Typography variant="caption" sx={{ color: '#90a4ae' }}>
          Acct {status.accountId} · {status.contractId}
        </Typography>
        <Chip
          label={`${status.openLots} lot${status.openLots !== 1 ? 's' : ''} open`}
          size="small"
          sx={{ bgcolor: '#1565c022', color: '#90caf9', border: '1px solid #1565c055', fontWeight: 600 }}
        />
        {sig && (
          <>
            {colorChip(sig.cloudTrend, CLOUD_COLORS)}
            {colorChip(sig.grade, GRADE_COLORS)}
            <Typography variant="caption" sx={{ color: '#b0bec5' }}>
              {sig.lastClose.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </>
        )}
        <Box sx={{ ml: 'auto' }}>
          <Typography variant="caption" sx={{ color: '#546e7a' }}>
            Win days: {status.persistedWindow.winningDays150}/5 ·
            Day net: {status.persistedWindow.netProfitSinceLastPayout >= 0 ? '+' : ''}
            ${status.persistedWindow.netProfitSinceLastPayout.toFixed(0)}
          </Typography>
        </Box>
      </Box>

      {/* Row 2: controls */}
      {accountId && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 0.5 }}>
          {/* Stop button */}
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={stopping ? <CircularProgress size={12} color="inherit" /> : <Stop fontSize="small" />}
            disabled={stopping || !isLive}
            onClick={handleStop}
            sx={{
              borderColor: '#c62828',
              color: '#ef5350',
              '&:hover': { bgcolor: '#c6282814', borderColor: '#ef5350' },
              fontWeight: 700,
              fontSize: '0.72rem',
              py: 0.4,
              px: 1.5,
              minWidth: 90,
            }}
          >
            {stopping ? 'Stopping…' : 'Stop Runner'}
          </Button>

          {/* Max contracts slider */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, maxWidth: 320 }}>
            <Typography variant="caption" sx={{ color: '#78909c', whiteSpace: 'nowrap', minWidth: 80 }}>
              Max contracts
            </Typography>
            <Slider
              value={localMaxLots}
              min={1}
              max={7}
              step={1}
              marks
              onChange={handleLotChange}
              disabled={savingLots || !isLive}
              size="small"
              sx={{
                color: '#1565c0',
                '& .MuiSlider-mark': { bgcolor: '#1e3a5f' },
                '& .MuiSlider-markActive': { bgcolor: '#90caf9' },
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: lotMsg === 'error' ? '#ef5350' : '#90caf9', minWidth: 36, fontWeight: 700, fontFamily: 'monospace' }}
            >
              {lotMsg ?? localMaxLots}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Open clips ────────────────────────────────────────────────────────────────

function OpenClips({ clips, lastClose }: { clips: RunnerStatus['clips']; lastClose: number }) {
  if (clips.length === 0) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" sx={{ color: '#90a4ae', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
        Open Clips
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
        {clips.map((c) => {
          const pts = lastClose > 0 && c.entryPrice > 0 ? lastClose - c.entryPrice : null;
          const pnl = pts !== null ? pts * 2 : null;
          const color = pnl === null ? '#90a4ae' : pnl >= 0 ? '#4caf50' : '#ef5350';
          return (
            <Paper key={c.clipId} sx={{ bgcolor: '#0d1b2a', border: '1px solid #1e3a5f', px: 1.5, py: 0.75, borderRadius: 1 }}>
              <Typography variant="caption" sx={{ color: '#b0bec5', display: 'block' }}>
                Entry {c.entryPrice.toFixed(2)}
              </Typography>
              {pnl !== null && (
                <Typography variant="caption" sx={{ color, fontWeight: 700 }}>
                  {pnl >= 0 ? '+' : ''}{pnl.toFixed(0)} USD ({pts!.toFixed(2)} pts)
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Decision log table ────────────────────────────────────────────────────────

function DecisionTable({ entries }: { entries: DecisionLogEntry[] }) {
  if (entries.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: '#546e7a' }}>
          No decisions yet — runner is scanning every 15s
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: 420, bgcolor: 'transparent' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {['Time', 'Action', 'Grade', 'Cloud', 'Signal', 'DOM', 'Price', 'Lots', 'Reason'].map(h => (
              <TableCell key={h} sx={{ bgcolor: '#0a1929', color: '#546e7a', fontSize: '0.68rem', fontWeight: 700, py: 0.75, borderBottom: '1px solid #1e3a5f' }}>
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((e, i) => (
            <TableRow
              key={`${e.ts}-${i}`}
              sx={{
                bgcolor: i === 0 ? '#0d1b2a' : 'transparent',
                '&:hover': { bgcolor: '#0d1b2a' },
                borderBottom: '1px solid #0d1b2a',
              }}
            >
              <TableCell sx={{ color: '#78909c', fontSize: '0.7rem', py: 0.5, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                {formatTime(e.ts)}
              </TableCell>
              <TableCell sx={{ py: 0.5 }}>
                {colorChip(e.action, ACTION_COLORS)}
              </TableCell>
              <TableCell sx={{ py: 0.5 }}>
                {colorChip(e.grade, GRADE_COLORS)}
              </TableCell>
              <TableCell sx={{ py: 0.5 }}>
                {colorChip(e.cloudTrend, CLOUD_COLORS)}
              </TableCell>
              <TableCell sx={{ color: '#90a4ae', fontSize: '0.68rem', py: 0.5 }}>
                {e.cloudSignal}
              </TableCell>
              <TableCell sx={{ color: e.domSignal === 'ABSORPTION' ? '#4caf50' : e.domSignal === 'BREAKDOWN' ? '#ef5350' : '#78909c', fontSize: '0.68rem', py: 0.5 }}>
                {e.domSignal}
              </TableCell>
              <TableCell sx={{ py: 0.5, fontFamily: 'monospace' }}>
                {e.livePrice && e.livePrice > 0 ? (
                  <span style={{ color: '#b0bec5', fontSize: '0.7rem' }}>
                    {e.livePrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                ) : e.lastClose > 0 ? (
                  <Tooltip title="3m bar close (DOM unavailable)" placement="top">
                    <span style={{ color: '#546e7a', fontSize: '0.7rem' }}>
                      {e.lastClose.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}*
                    </span>
                  </Tooltip>
                ) : '—'}
              </TableCell>
              <TableCell sx={{ color: '#90caf9', fontSize: '0.7rem', py: 0.5, textAlign: 'center' }}>
                {e.openLots}
              </TableCell>
              <TableCell sx={{ color: '#546e7a', fontSize: '0.65rem', py: 0.5, maxWidth: 260 }}>
                <Tooltip title={e.chainReason} placement="top-start">
                  <span style={{ cursor: 'default', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>
                    {e.blockedAt ? `[${e.blockedAt}] ` : ''}{e.chainReason}
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const POLL_MS = 15_000;

interface ProfileCDecisionLogProps {
  /** If provided, fetches from /runners/:accountId (funded account). Omit for combine. */
  accountId?: number;
  label?: string;
}

const ProfileCDecisionLog: React.FC<ProfileCDecisionLogProps> = ({ accountId, label }) => {
  const [status, setStatus] = useState<RunnerStatus | null>(null);
  const [error, setError]   = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const endpoint = accountId
    ? `/topstep/profile-c/runners/${accountId}`
    : '/topstep/profile-c/runner-status';

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get(endpoint);
      // /runners/:accountId returns { success, status }; /runner-status returns { success, status }
      const data = res.data?.status ?? res.data;
      if (res.data?.success && data) {
        setStatus(data as RunnerStatus);
        setError(null);
      } else {
        setError(res.data?.error ?? 'Unknown error');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to fetch runner status');
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, [endpoint]);

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(t);
  }, [fetchStatus]);

  return (
    <Box sx={{ bgcolor: '#070f1a', border: '1px solid #1e3a5f', borderRadius: 2, p: 2, mt: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ color: '#e3f2fd', fontWeight: 700, letterSpacing: 0.5, flexGrow: 1 }}>
          PROFILE C · {label ?? 'COMBINE RUNNER'} · DECISION LOG
        </Typography>
        <Typography variant="caption" sx={{ color: '#37474f', mr: 1 }}>
          {lastRefresh ? `updated ${formatTime(lastRefresh.toISOString())}` : ''}
        </Typography>
        <IconButton size="small" onClick={fetchStatus} sx={{ color: '#546e7a' }}>
          <Refresh fontSize="small" />
        </IconButton>
      </Box>

      {loading && <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>}

      {error && !loading && (
        <Alert severity="warning" sx={{ mb: 2, bgcolor: '#1a1200', color: '#ffb74d', fontSize: '0.75rem' }}>
          {error}
        </Alert>
      )}

      {status && !loading && (
        <>
          <RunnerStatusBar status={status} accountId={accountId} onRefresh={fetchStatus} />
          <OpenClips
            clips={status.clips}
            lastClose={status.cachedSignal?.lastClose ?? 0}
          />
          <Typography variant="caption" sx={{ color: '#37474f', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
            Scan History (last {status.decisionLog.length})
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <DecisionTable entries={status.decisionLog} />
          </Box>
        </>
      )}
    </Box>
  );
};

export default ProfileCDecisionLog;
